import { Clock3, ExternalLink, MapPin } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { AgentVehicle, TravelLeg } from "@/lib/types/vehicle-agent";
import { formatTravelDistance, formatTravelDuration } from "@/lib/vehicle-travel";

const TravelMetric = ({ label, leg, compact = false }: { label: string; leg: TravelLeg; compact?: boolean }) => {
  const locale = useLocale();

  if (compact) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl bg-secondary-light px-3 py-2.5">
        <p className="text-xs font-semibold text-black/50">{label}</p>
        <div className="flex shrink-0 items-center gap-2" dir="ltr">
          <span className="flex items-center gap-1 font-urbanist text-sm font-bold text-primary">
            <Clock3 className="h-3.5 w-3.5" aria-hidden="true" />
            {formatTravelDuration(leg.durationSeconds, locale)}
          </span>
          <span className="text-xs font-semibold text-black/55">
            {formatTravelDistance(leg.distanceMeters, locale)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-secondary-light p-4">
      <p className="text-xs font-semibold text-black/50">{label}</p>
      <div className="mt-2 flex flex-wrap items-end gap-x-3 gap-y-1">
        <span className="flex items-center gap-1.5 font-urbanist text-lg font-bold text-primary">
          <Clock3 className="h-4 w-4" aria-hidden="true" />
          {formatTravelDuration(leg.durationSeconds, locale)}
        </span>
        <span className="text-sm font-semibold text-black/60">
          {formatTravelDistance(leg.distanceMeters, locale)}
        </span>
      </div>
    </div>
  );
};

const CarLocationCard = ({ car, compact = false }: { car: AgentVehicle; compact?: boolean }) => {
  const t = useTranslations("CarDetails.location");
  const locationLabel = car.locationLabel || car.locationDistance?.vehicleLocation.label;
  const distance = car.locationDistance;
  const mapsUrl =
    car.locationGoogleMapsUrl ||
    (car.locationLatitude != null && car.locationLongitude != null
      ? `https://www.google.com/maps/search/?api=1&query=${car.locationLatitude},${car.locationLongitude}`
      : null);

  if (!locationLabel && !distance) {
    return null;
  }

  const headingId = compact ? "vehicle-location-heading-sidebar" : "vehicle-location-heading-content";

  return (
    <section
      className={compact ? "rounded-3xl bg-white p-4" : "rounded-3xl bg-white p-5 sm:p-7"}
      aria-labelledby={headingId}
    >
      <div
        className={
          compact
            ? "flex items-center justify-between gap-3"
            : "flex flex-col justify-between gap-4 sm:flex-row sm:items-center"
        }
      >
        <div>
          <div className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-light text-primary">
              <MapPin className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h2 id={headingId} className="font-urbanist text-xl font-bold text-primary-foreground">
                {compact ? locationLabel || t("locationUnknown") : t("title")}
              </h2>
              {!compact && locationLabel ? <p className="font-inter text-sm text-black/60">{locationLabel}</p> : null}
            </div>
          </div>
        </div>

        {mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={
              compact
                ? "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-primary-light px-2.5 py-2 font-inter text-xs font-semibold text-primary transition-colors duration-300 hover:bg-primary hover:text-white"
                : "inline-flex items-center justify-center gap-2 rounded-xl bg-primary-light px-4 py-2.5 font-inter text-sm font-semibold text-primary transition-colors duration-300 hover:bg-primary hover:text-white"
            }
            aria-label={t("openMapsAria", { location: locationLabel || t("vehicle") })}
          >
            {t("openMaps")}
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        ) : null}
      </div>

      {distance ? (
        <>
          <div className={compact ? "mt-4" : "mt-5"}>
            <TravelMetric label={t("fullTrip")} leg={distance.roundTrip} compact={compact} />
          </div>
          <p className="mt-3 font-inter text-xs text-black/45">{t("trafficConditions")}</p>
        </>
      ) : null}
    </section>
  );
};

export default CarLocationCard;
